import { createSlice } from "@reduxjs/toolkit";
import { setTimezoneRequest, setTimezoneSuccess, setTimezoneFailure, clearTimezone, setTimezoneFromId } from './timezoneSlice';
import addressReducer from './addressSlice';

const TZ_LABELS = {
  'America/New_York': 'Eastern Time (ET)',
  'America/Chicago': 'Central Time (CT)',
  'America/Denver': 'Mountain Time (MT)',
  'America/Phoenix': 'Mountain Time (MT)',
  'America/Los_Angeles': 'Pacific Time (PT)',
  'America/Anchorage': 'Alaska Time (AKT)',
  'Pacific/Honolulu': 'Hawaii Time (HT)',
  'Asia/Kolkata': 'India Time (IST)',
  'Europe/London': 'UK Time (GMT/BST)',
  'Europe/Athens': 'Greece Time (EET)',
  'Africa/Nairobi': 'Kenya Time (EAT)',
  'America/Toronto': 'Eastern Time (ET)',
};

function idToLabel(id) {
  if (!id) return '';
  return TZ_LABELS[id] || id;
}

const initialState = {
  fields: [],
  fieldsLoading: false,
  fieldsError: null,

  carriers: [],
  carriersLoading: false,
  carriersError: null,
  carriersLoaded: false,

  createWorkkard: {
    loading: false,
    error: null,
    fieldErrors: [],
    result: null,
    // Creation is asynchronous: the POST only acknowledges validation. These
    // track the wait for the projected claim to become readable.
    pendingRequestId: null,
    workkardId: null,
    readyWorkkardId: null,
  },

  fetchWorkkard: {
    loading: false,
    error: null,
    data: null,
  },

  updateWorkkard: {
    loading: false,
    error: null,
    fieldErrors: [],
    result: null,
  },

  timezone: {
    id: null,
    displayText: '',
    loading: false,
  },

  address: addressReducer(undefined, { type: '@@INIT' }),
};

const workkardFormSlice = createSlice({
  name: "workkardForm",
  initialState,
  reducers: {
    fetchWorkkardFieldsRequest: (state) => {
      state.fieldsLoading = true;
      state.fieldsError = null;
    },
    fetchWorkkardFieldsSuccess: (state, action) => {
      state.fieldsLoading = false;
      state.fields = action.payload;
    },
    fetchWorkkardFieldsError: (state, action) => {
      state.fieldsLoading = false;
      state.fieldsError = action.payload;
    },
    clearWorkkardFields: (state) => {
      state.fields = [];
      state.fieldsLoading = false;
      state.fieldsError = null;
    },
    fetchCarriersRequest: (state) => {
      state.carriersLoading = true;
      state.carriersError = null;
    },
    fetchCarriersSuccess: (state, action) => {
      state.carriersLoading = false;
      state.carriers = action.payload;
      state.carriersLoaded = true;
    },
    fetchCarriersError: (state, action) => {
      state.carriersLoading = false;
      state.carriersError = action.payload;
      state.carriersLoaded = true;
    },
    createWorkkardRequest: (state) => {
      state.createWorkkard.loading = true;
      state.createWorkkard.error = null;
      state.createWorkkard.fieldErrors = [];
      state.createWorkkard.result = null;
      state.createWorkkard.pendingRequestId = null;
      state.createWorkkard.workkardId = null;
      state.createWorkkard.readyWorkkardId = null;
    },
    // The gateway accepted the command. Loading deliberately stays true: the
    // claim is not readable yet, and redirecting now lands on an empty page.
    createWorkkardAccepted: (state, action) => {
      state.createWorkkard.pendingRequestId = action.payload?.requestId ?? null;
      state.createWorkkard.workkardId = action.payload?.workkardId ?? null;
    },
    // The projection exists -- either the socket said so, or the poll found it.
    createWorkkardReady: (state, action) => {
      state.createWorkkard.loading = false;
      state.createWorkkard.readyWorkkardId = action.payload?.workkardId ?? null;
    },
    createWorkkardSuccess: (state, action) => {
      state.createWorkkard.loading = false;
      state.createWorkkard.result = action.payload;
    },
    createWorkkardError: (state, action) => {
      state.createWorkkard.loading = false;
      const payload = action.payload;
      if (payload && typeof payload === 'object') {
        state.createWorkkard.error = payload.message ?? null;
        state.createWorkkard.fieldErrors = payload.fields ?? [];
      } else {
        state.createWorkkard.error = payload;
        state.createWorkkard.fieldErrors = [];
      }
    },
    resetCreateWorkkard: (state) => {
      state.createWorkkard = {
        loading: false,
        error: null,
        fieldErrors: [],
        result: null,
        pendingRequestId: null,
        workkardId: null,
        readyWorkkardId: null,
      };
    },
    fetchWorkkardByIdRequest: (state) => {
      state.fetchWorkkard.loading = true;
      state.fetchWorkkard.error = null;
    },
    fetchWorkkardByIdSuccess: (state, action) => {
      state.fetchWorkkard.loading = false;
      state.fetchWorkkard.data = action.payload;
    },
    fetchWorkkardByIdError: (state, action) => {
      state.fetchWorkkard.loading = false;
      state.fetchWorkkard.error = action.payload;
    },
    updateWorkkardRequest: (state) => {
      state.updateWorkkard.loading = true;
      state.updateWorkkard.error = null;
      state.updateWorkkard.fieldErrors = [];
      state.updateWorkkard.result = null;
    },
    updateWorkkardSuccess: (state, action) => {
      state.updateWorkkard.loading = false;
      state.updateWorkkard.result = action.payload;
    },
    updateWorkkardError: (state, action) => {
      state.updateWorkkard.loading = false;
      const payload = action.payload;
      if (payload && typeof payload === 'object') {
        state.updateWorkkard.error = payload.message ?? null;
        state.updateWorkkard.fieldErrors = payload.fields ?? [];
      } else {
        state.updateWorkkard.error = payload;
        state.updateWorkkard.fieldErrors = [];
      }
    },
    resetUpdateWorkkard: (state) => {
      state.updateWorkkard = { loading: false, error: null, fieldErrors: [], result: null };
    },
    resetWorkkardForm: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(setTimezoneRequest, (state, action) => {
        state.timezone.loading = true;
      })
      .addCase(setTimezoneSuccess, (state, action) => {
        state.timezone.id = action.payload.id;
        state.timezone.displayText = action.payload.displayText;
        state.timezone.loading = false;
      })
      .addCase(setTimezoneFailure, (state) => {
        state.timezone.loading = false;
      })
      .addCase(clearTimezone, (state) => {
        state.timezone.id = null;
        state.timezone.displayText = '';
        state.timezone.loading = false;
      })
      .addCase(setTimezoneFromId, (state, action) => {
        const id = action.payload?.timeZoneId || null;
        state.timezone.id = id;
        state.timezone.displayText = idToLabel(id);
        state.timezone.loading = false;
      })
      .addMatcher(
        (action) => typeof action.type === 'string' && action.type.startsWith('workkardForm/address/'),
        (state, action) => {
          state.address = addressReducer(state.address, action);
        },
      );
  },
});

export const {
  fetchWorkkardFieldsRequest,
  fetchWorkkardFieldsSuccess,
  fetchWorkkardFieldsError,
  clearWorkkardFields,
  fetchCarriersRequest,
  fetchCarriersSuccess,
  fetchCarriersError,
  createWorkkardRequest,
  createWorkkardAccepted,
  createWorkkardReady,
  createWorkkardSuccess,
  createWorkkardError,
  resetCreateWorkkard,
  resetUpdateWorkkard,
  fetchWorkkardByIdRequest,
  fetchWorkkardByIdSuccess,
  fetchWorkkardByIdError,
  updateWorkkardRequest,
  updateWorkkardSuccess,
  updateWorkkardError,
  resetWorkkardForm,
} = workkardFormSlice.actions;

export default workkardFormSlice.reducer;
